import type { AppError, AppErrorKind } from "./types";

// Default error messages for each AppErrorKind
export const DEFAULT_ERROR_MESSAGES: Record<AppErrorKind, string> = {
  validation: "יש לתקן את השדות המסומנים.",
  auth: "נדרשת התחברות מחדש.",
  forbidden: "אין לך הרשאה לבצע פעולה זו.",
  notFound: "המשאב המבוקש לא נמצא.",
  conflict: "לא ניתן להשלים את הפעולה בגלל התנגשות בנתונים.",
  business: "לא ניתן להשלים את הפעולה.",
  network: "בעיית רשת. נסה שוב.",
  timeout: "הבקשה נמשכת יותר מדי זמן. נסה שוב.",
  csrf: "פג תוקף האבטחה של הבקשה. נסה שוב.",
  server: "אירעה שגיאת שרת.",
  unexpected: "אירעה שגיאה לא צפויה.",
};

// Additional error messages based on specific error message codes
export const CODE_BASED_ERROR_MESSAGES: Record<string, string> = {
  AUTH_UNAUTHENTICATED: "נדרשת התחברות מחדש.",
  AUTH_INVALID_CREDENTIALS: "פרטי ההתחברות אינם נכונים.",
  AUTH_FORBIDDEN: "אין לך הרשאה לבצע פעולה זו.",
  VALIDATION_FAILED: "יש לתקן את השדות המסומנים.",
  RESOURCE_NOT_FOUND: "המידע המבוקש לא נמצא.",
  BUSINESS_RULE_VIOLATION: "לא ניתן להשלים את הפעולה.",
  DATABASE_CONSTRAINT_VIOLATION: "לא ניתן להשלים את הפעולה בגלל התנגשות בנתונים.",
  DATABASE_QUERY_ERROR: "אירעה שגיאת שרת. נסה שוב בעוד כמה רגעים.",
  HTTP_ERROR: "הבקשה נכשלה.",
  INTERNAL_SERVER_ERROR: "אירעה שגיאת שרת. נסה שוב בעוד כמה רגעים.",
};


/**
 * Returns a user-friendly error message based on the provided AppErrorKind.
 * @param kind 
 * @returns 
 */
export function getErrorMessage(kind: AppErrorKind): string {
  return DEFAULT_ERROR_MESSAGES[kind];
}


/**
 * Returns a fallback Hebrew error message based on the HTTP status code number.
 * @param status number | undefined - The HTTP status code from the server response.
 * @returns string | undefined - A user-friendly error message, or undefined if the status code is not recognized.
 */
function getStatusFallbackMessage(status: number | undefined): string | undefined {
  if (status === 401) return CODE_BASED_ERROR_MESSAGES.AUTH_UNAUTHENTICATED;
  if (status === 403) return CODE_BASED_ERROR_MESSAGES.AUTH_FORBIDDEN;
  if (status === 404) return CODE_BASED_ERROR_MESSAGES.RESOURCE_NOT_FOUND;
  if (status === 409) return CODE_BASED_ERROR_MESSAGES.DATABASE_CONSTRAINT_VIOLATION;
  if (status === 422) return CODE_BASED_ERROR_MESSAGES.VALIDATION_FAILED;
  if (typeof status === "number" && status >= 500) {
    return CODE_BASED_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }

  return undefined;
}



/**
 * Resolves the user-friendly message for a given error based on its properties.
 * The function first checks if there's a specific message for the error code message,
 * then falls back to a message based on the HTTP status code,
 * and finally defaults to a message based on the error kind.
 * @param error 
 * @returns String error message to be displayed.
 */
export function resolveUserMessage(error: Pick<AppError, "kind" | "code" | "status">): string {
  
  // First, try to get a message based on the error code message if it exists
  if (error.code && CODE_BASED_ERROR_MESSAGES[error.code]) {
    return CODE_BASED_ERROR_MESSAGES[error.code];
  }

  // If no code-based message is found, try to get a message based on the HTTP status number
  const statusFallback = getStatusFallbackMessage(error.status);

  if (statusFallback) {
    return statusFallback;
  }

  // If neither code nor status provided a message, return the default message based on kind
  return getErrorMessage(error.kind);
}
